import { msg } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { useMainLayout } from 'modules/app-shell/ui/layout/context';
import { Pages } from 'modules/navigation/domain/enums/pages';
import { Player } from 'modules/players/domain/interfaces/player';
import { usePlayerList } from 'modules/players/ui/hooks/use-player-list';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { USER_DEFAULT_AVATAR } from 'shared/constants/links';
import { useToastApiError } from 'shared/hooks/use-toast-api-error';
import { getBirthDay, getFullName } from 'shared/utils';
import UDButton from 'ud-ui/components/button';
import HeaderContentCategory from 'ud-ui/components/category/components/list-wrapper/header-content';
import Modal from 'ud-ui/components/modal';
import { UDEntityRow, UDPagedList } from 'ud-ui/components/paged-list';

function PlayerListPage() {
  const { t } = useLingui();
  const navigate = useNavigate();
  const { setHeaderContent } = useMainLayout();

  const createButtonText = t`Добавить игрока`;

  const handleCreateClick = useCallback(() => {
    navigate(Pages.PLAYERS.CREATE);
  }, [navigate]);

  const {
    items,
    itemsOffset,
    state,
    page,
    totalPages,
    perPage,
    draftSearch,
    setPage,
    setPerPage,
    setSearch,
    buildPageHref,
    deletePlayer,
  } = usePlayerList();

  const toastApiError = useToastApiError();
  const [playerToDelete, setPlayerToDelete] = useState<Player | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const openDelete = useCallback((player: Player) => {
    setPlayerToDelete(player);
  }, []);
  const closeDelete = useCallback(() => {
    setPlayerToDelete(null);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!playerToDelete) return;
    setIsDeleting(true);
    try {
      await deletePlayer(playerToDelete.id);
      setPlayerToDelete(null);
      toast.success(t`Игрок успешно удалён`);
    } catch (error) {
      toastApiError(error, msg`Не удалось удалить игрока`);
    } finally {
      setIsDeleting(false);
    }
  }, [deletePlayer, playerToDelete, t, toastApiError]);

  const handlePlayerClick = useCallback(
    (player: Player) => {
      navigate(Pages.PLAYERS.builders.view(player.id));
    },
    [navigate],
  );

  useEffect(() => {
    setHeaderContent(
      <HeaderContentCategory title={t`Игроки`} createButtonText={createButtonText} createButtonOnClick={handleCreateClick} />,
    );
    return () => setHeaderContent(null);
  }, [createButtonText, handleCreateClick, setHeaderContent, t]);

  // Номер строки продолжается со страницы на страницу и считается от `itemsOffset`,
  // а не от `page`: пока едет следующая страница, на экране ещё прошлые элементы.
  const renderPlayer = useCallback(
    (player: Player, index: number) => {
      const height = player.height;
      const weight = player.weight;

      return (
        <UDEntityRow
          index={itemsOffset + index + 1}
          avatar={player.photo?.externalUrl ?? USER_DEFAULT_AVATAR}
          title={getFullName(player)}
          subtitle={
            <span className="flex gap-4">
              <span>{getBirthDay({ birthDate: player.birthDate ? new Date(player.birthDate) : undefined })}</span>
              <span>
                <Trans>Рост:</Trans> {height ? t`${height} см` : '-'}
              </span>
              <span>
                <Trans>Вес:</Trans> {weight ? t`${weight} кг` : '-'}
              </span>
            </span>
          }
          actions={
            <UDButton variant="secondary" style={{ width: 144 }} onClick={() => openDelete(player)}>
              <Trans>Удалить</Trans>
            </UDButton>
          }
          onClick={() => handlePlayerClick(player)}
        />
      );
    },
    [handlePlayerClick, itemsOffset, openDelete, t],
  );

  const playerToDeleteName = playerToDelete ? getFullName(playerToDelete) : '';

  return (
    <>
      <UDPagedList<Player>
        items={items}
        renderItem={renderPlayer}
        getItemKey={(player) => player.id}
        state={state}
        page={page}
        totalPages={totalPages}
        perPage={perPage}
        onPageChange={setPage}
        onPerPageChange={setPerPage}
        pageHref={buildPageHref}
        search={draftSearch}
        onSearchChange={setSearch}
        searchPlaceholder={t`Поиск по игрокам`}
        emptyText={t`Игроков пока нет`}
        emptyAction={
          <UDButton variant="primary" onClick={handleCreateClick}>
            {createButtonText}
          </UDButton>
        }
        notFoundText={t`Игроки не найдены`}
      />

      <Modal.Confirm
        show={Boolean(playerToDelete)}
        title={t`Удалить игрока «${playerToDeleteName}»?`}
        description={t`Это действие необратимо.`}
        isLoading={isDeleting}
        onConfirm={handleConfirmDelete}
        onCancel={closeDelete}
      />
    </>
  );
}

export default PlayerListPage;
